/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ProfileNav from './components/ProfileNav'
import ProfileTools from './components/ProfileTools.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { profileSignal, profileLoadedSignal, profileCcSchemaSignal, profileErrorSignal } from '../../state/signals/profileSignals'
import { profileSchemaSignal, profileSchemaLoadedSignal, profileSchemaVersionSignal, profileSchemaErrorSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import { isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ProfileConfig() {

    const navigate = useNavigate()
    const { beId, profileId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    useEffect(() => {
        if (beIdChanged) return
        fetchProfile(beId, profileId, config, accessToken).then(
            () => {
                fetchSchemaManifest().then(
                    () => {
                        fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                    }
                )
            }
        )
    }, [beId, beIdChanged, profileId, config, accessToken])

    const jsonFormDataComputed = useComputed(() => {
        if (!profileLoadedSignal.value || !profileSchemaLoadedSignal.value) {
            return null
        }
        else if (!isEmptyObject(profileSchemaSignal.value) && !isEmptyObject(profileSignal.value)) {
        
            // deep clone the schema
            let clonedSchema = structuredClone(profileSchemaSignal.value)

            // remove the api interfaces from the schema since those are edited in a separate page
            delete clonedSchema.properties.config.properties.apiInterfaceConfig

            // disable editing of all fields if canEditComputed is false
            if (!canEditComputed.value) {
                clonedSchema = makeFieldsReadonly(clonedSchema)
            }

            // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
            globalThis.SCHEMA_MODE = 'PROFILE'
            //console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)
            
            // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
            const formData = {
                schema: clonedSchema.properties.config,
                value: profileSignal.value.config,
                loadedComponentConfig: profileSignal.value.config,
                onSubmitValid: handleFormSubmit,
                onCancel: handleCancel,
                onFormChange: handleFormChange,
                canEditConfig: canEditComputed.value
            }
            //console.log('formData from config', formData)
            return formData
        }
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const accordionData = [
        { name: 'Profile Data', data: profileSignal.value },
        { name: 'Profile Schema', data: profileSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    const handleFormSubmit = values => {
        try {
            //console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profileSignal.value.id,
                    name: profileSignal.value.name,
                    businessEntityId: profileSignal.value.businessEntityId,
                    config: values,
                }
                data.config.apiInterfaceConfig = profileSignal.value.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []
                data.config.deviceConfig = profileSignal.value.config.deviceConfig || {}
                data.config.deviceConfig.device = data.config.deviceConfig.device || []
                
                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileSchemaVersionSignal.value

                //console.log(data)
                updateProfile(beId, profileId, data, config, accessToken, true)
                    .then(() => {
                        addNotification('Profile was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification(`Unable to update the Profile. ${error.toString()}`, NotificationTypes.ERROR, false)
                        return false
                    })
                    .finally(() => {
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    return (
        <>
            <ConfirmationModal show={showCancelModalSignal.value} handleCancel={() => showCancelModalSignal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    profileLoadedSignal.value ? <>Manage {profileSignal.value.name}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='Profile Configuration' canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal]} errorSignals={[profileErrorSignal]}>
                   <ProfileTools profile={profileSignal.value}></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="4"></ProfileNav>
            <Loadable loadedSignals={[profileLoadedSignal, profileSchemaLoadedSignal]} errorSignals={[profileErrorSignal, profileSchemaErrorSignal]}>
                <JsonForm data={jsonFormDataComputed.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}